import base64url from "base64url";
import html2canvas from "html2canvas";
import { Parser } from "json2csv";

const debug = require("debug");

/**
 *
 * @param obj
 * @returns {boolean}
 */
const isEmpty = (obj) => Object.keys(obj || {}).length === 0;

/**
 *
 * @param obj
 * @returns {boolean}
 */
const isNotEmpty = (obj) => !isEmpty(obj);

/**
 *
 * @param obj
 * @returns {boolean}
 */
const isTrue = (obj) =>
  `${obj}`.toLowerCase().trim() === "true" ||
  `${obj}`.toLowerCase().trim() === "1";

/**
 *
 * @param num
 * @returns {boolean}
 */
const isNumeric = (num) => {
  const intN = parseInt(num, 10);
  return typeof intN === "number" && !Number.isNaN(intN);
};

/**
 *
 * @param obj
 * @returns {boolean}
 */
const isFunction = (obj) => typeof obj === "function";

/**
 *
 * @param keys
 * @param obj
 * @param defVal
 * @returns {*}
 */
const safeGet = (obj, keys, defVal = null) =>
  keys.reduce(
    (prevObj, key) => (prevObj && prevObj[key] ? prevObj[key] : defVal),
    obj
  );

const safeArray = (obj) => (Array.isArray(obj) ? obj : [obj]);

/**
 * 禁止用在大的 object 上
 * @param obj1
 * @param obj2
 * @returns {boolean}
 */
const isEquals = (obj1, obj2) => {
  if ((isEmpty(obj1) && !isEmpty(obj2)) || (!isEmpty(obj1) && isEmpty(obj2))) {
    return false;
  }
  //
  const o1Keys = Object.keys(obj1);
  const o2Keys = Object.keys(obj2);
  if (o1Keys.length !== o2Keys.length) {
    return false;
  }
  //
  // eslint-disable-next-line consistent-return
  o1Keys.forEach((key) => {
    if (!o2Keys.includes(key)) {
      return false;
    }
  });
  // eslint-disable-next-line consistent-return
  o2Keys.forEach((key) => {
    if (!o1Keys.includes(key)) {
      return false;
    }
  });
  //
  const sortedKeys = [...new Set([...o1Keys, ...o2Keys].sort())];
  const newObj1 = JSON.stringify(
    sortedKeys.map((key) => ({ [key]: obj1[key] }))
  );
  const newObj2 = JSON.stringify(
    sortedKeys.map((key) => ({ [key]: obj2[key] }))
  );
  return newObj1 === newObj2;
};

// check if values in obj all empty
const leastOneValueEmpty = (obj) => {
  // eslint-disable-next-line no-underscore-dangle
  let _isEmpty = false;
  const values = Object.values(obj || {});
  values.forEach((v) => {
    if (!v) _isEmpty = true;
  });
  return values.length === 0 || _isEmpty;
};

const getDate = () => {
  const nowDate = new Date();
  const mm = nowDate.getMonth() + 1;
  const dd = nowDate.getDate();
  return [
    nowDate.getFullYear() - 1911,
    (mm > 9 ? "" : "0") + mm,
    (dd > 9 ? "" : "0") + dd,
  ].join("-");
};

const getTimeSince = (date) => {
  if (typeof date !== "object") {
    // eslint-disable-next-line no-param-reassign
    date = new Date(date);
  }

  const seconds = Math.floor((new Date() - date) / 1000);
  let intervalType = "";
  let interval = Math.floor(seconds / 31536000);

  if (interval >= 1) {
    intervalType = "year";
  } else {
    interval = Math.floor(seconds / 2592000);
    if (interval >= 1) {
      intervalType = "month";
    } else {
      interval = Math.floor(seconds / 86400);
      if (interval >= 1) {
        intervalType = "day";
      } else {
        interval = Math.floor(seconds / 3600);
        if (interval >= 1) {
          intervalType = "hour";
        } else {
          interval = Math.floor(seconds / 60);
          if (interval >= 1) {
            intervalType = "minute";
          } else {
            interval = seconds;
            intervalType = "second";
          }
        }
      }
    }
  }

  if (interval > 1 || interval === 0) {
    intervalType += "s";
  }

  return `${interval} ${intervalType} age`;
};

const getYear = (date) => {
  if (!date) return null;
  const reg = /\d+/;
  /**
	 * ca(大約);  fl(全盛時期); active
     date => match
     1999 => [1999]
     888 => [888]
     ca. 1008 => [1008]
     fl. 1007 => [1007]
     active 1146 => [1146]
     1008 or 1009  => [1008, 1009]
     1920 June 16  => [1920, 16]
     1927 April  => [1927]
	 */

  const match = date.match(reg);
  if (match) return match[0];
  return null;
};

const ISOtransformYMD = (ISOstring) => {
  const time = new Date(ISOstring);
  // month 必須 +1
  return `${time.getFullYear()}-${time.getMonth() + 1}-${time.getDate()}`;
};

const getFormatUser = (user) => {
  const { providerData, metadata } = user;
  const safeProviderId =
    providerData && providerData[0]
      ? providerData && providerData[0].providerId
      : "unKnown";
  const { creationTime, lastSignInTime } = metadata;
  return {
    ...user,
    providerId: safeProviderId,
    creationTime,
    lastSignInTime,
  };
};

const genRandomKey = (length) => {
  let result = "";
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;
  for (let i = 0; i < length; i += 1) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

const handleNameSpellCheck = (_name) => {
  let name = _name;
  if (isEmpty(name)) {
    return "";
  }
  // bad_chars_list convert list
  const badCharsChange = {
    " ": "_",
    "^": "_",
    "\\\\": "_",
    "|": "_",
    "<": "_",
    ">": "_",
    "{": "_",
    "}": "_",
    '"': "_",
    "`": "_",
  };
  if (name.length > 0) name = name.trim();
  // # remove bad_chars
  Object.keys(badCharsChange).forEach((bc) => {
    const cvtChar = badCharsChange[bc];
    if (name && name.indexOf(bc) >= 0) {
      name = `${name || ""}`.replace(new RegExp(bc, "g"), cvtChar);
    }
  });

  return name.replace(/\\/gm, "_");
};

/**
 * encode url (include dot .) to prevent path match error
 * e.g. "abc.def"  => "abc%2Edef"
 *
 * @param url
 * @returns {string}
 */
const encodeUrl = (url) => encodeURIComponent(url).replace(/\./g, "%2E");

// base64 encode url
const bs64Encode = (word) => base64url.encode(word);

// base64 decode url
const bs64Decode = (word) => base64url.decode(word);

// base64 encode with prefix id
const bs64EncodeId = (prefixId) => {
  if (!prefixId || prefixId.length <= 3) {
    return null;
  }
  const prefix = `${prefixId}`.slice(0, 3);
  const idNumber = `${prefixId}`.slice(3);
  return `${prefix}${bs64Encode(idNumber)}`;
};

// base64 decode with prefix id
const bs64DecodeId = (prefixId) => {
  if (!prefixId || prefixId.length <= 3) {
    return null;
  }
  const prefix = `${prefixId}`.slice(0, 3);
  const idNumber = `${prefixId}`.slice(3);
  return `${prefix}${bs64Decode(idNumber)}`;
};

const exportImage = async (element, options = {}) => {
  try {
    const fileName = options.fileName || "image.png";
    const resolution = options.resolution || 3;

    const opts = {
      useCORS: true,
      scale: resolution,
      scrollX: 0,
      scrollY: -window.scrollY, // fix scroll bottom problem
    };
    if (options.width) {
      opts.width = options.width + 50;
    }
    if (options.height) {
      opts.height = options.height + 50;
    }

    const getMineType = () => {
      if (options?.mineType) return options.mineType;
      return "image/png";
    };
    const minetype = getMineType();

    const canvas = await html2canvas(element, opts);
    const url = canvas
      .toDataURL(minetype, 1)
      .replace(minetype, "image/octet-stream");
    const aTag = document.createElement("a");
    aTag.href = url;
    aTag.download = fileName;
    document.body.appendChild(aTag);
    aTag.click();
    aTag.remove();
    // export image successfully
    return Promise.resolve({ finish: true });
  } catch (err) {
    // failed to export image
    return Promise.reject(err);
  }
};

// set download blob function for file export
const downloadBlob = (blob, filename) => {
  // Create an object URL for the blob object
  const url = URL.createObjectURL(blob);
  // Create a new anchor element
  const aTag = document.createElement("a");
  aTag.href = url;
  aTag.download = filename || "download";
  document.body.appendChild(aTag);
  aTag.click();
  aTag.remove();
};

// export data and download automatically
const exportDataToCsv = (data, options = {}) => {
  // if data is illegal
  if (typeof data !== "object") throw new Error("data is illegal.");
  // define field
  let fields;
  if (options.fields) {
    if (!Array.isArray(options.fields))
      throw new Error("fields must be array.");
    // eslint-disable-next-line prefer-destructuring
    fields = options.fields;
  } else {
    // if data is Array
    if (Array.isArray(data)) fields = Object.keys(data[0]);
    // if data is object
    if (!Array.isArray(data)) fields = Object.keys(data);
  }

  try {
    // json to csv
    const json2csvParser = new Parser({
      delimiter: options.delimiter || "\t",
      fields, // toplevel JSON attributes
      encoding: "utf8",
    });
    const csv = json2csvParser.parse(data);
    // create blob
    const csvToBlob = new Blob([csv], { type: "text/csv;charset=utf8" });
    const fileName = `meet-count-${new Date().getTime()}.csv`;
    // direct download
    downloadBlob(csvToBlob, fileName);
    if (options.onSuccess && typeof options.onSuccess === "function") {
      options.onSuccess();
    }
  } catch (err) {
    if (options.onError && typeof options.onError === "function") {
      options.onError(err);
    }
  }
};

// 針對 dataset 名稱, 那些語系情況下需要變更 dataset 名稱
const GRAPH_LOCALE_CHANGE = ["zh"];
/**
 * 轉換 dataset 的中英文名稱
 * @param names: ["auda_spc", "hkwrpr"] or "auda_spc"
 * @param datasets: [
 * { dataset: "abcwhkp", label: "《香港古典詩文集經眼錄》", lang: "zh" },
 * { dataset: "hklit", label: "香港文學作家傳略", lang: "zh" },...
 * ]
 * @param chLocale: 那些語系需要變更
 * @returns: ["", ""]
 */
const cvtDatasetLocale = (
  names = [],
  datasets = [],
  chLocale = GRAPH_LOCALE_CHANGE
) => {
  if (!Array.isArray(datasets)) return names;
  const datasetMap = datasets.reduce((acc, cur, curIdx) => {
    if (!cur.dataset) return acc;
    acc[cur.dataset] = { ...cur };
    return acc;
  }, {});

  if (Array.isArray(names)) {
    const namesLocale = Object.assign([], names);
    return namesLocale.map((name) =>
      datasetMap[name] &&
      datasetMap[name].label &&
      chLocale.indexOf(datasetMap[name].lang) >= 0
        ? datasetMap[name].label
        : name
    );
  }
  return datasetMap[names] &&
    datasetMap[names].label &&
    chLocale.indexOf(datasetMap[names].lang) >= 0
    ? datasetMap[names].label
    : names;
};

/**
 * 判斷是否為正式站 db, 若不包含關鍵字, 就不是正式站 db
 *
 * @param dbObj => e.g. {dbName: "正式站", label:"hkbdb"}
 * {dbName: "測式站", label:"hkbdb2"}
 * @returns {boolean}
 */
const isProductionDb = (dbObj) => {
  if (!dbObj) return true;
  if (
    dbObj &&
    dbObj.dbName &&
    (dbObj.dbName.includes("正式") ||
      dbObj.dbName === "db正式站" ||
      dbObj.label === "hkbdb")
  )
    return true;
  return false;
};

// transform ms to mm:ss
const millisToMMSS = (millis) => {
  const minutes = Math.floor(millis / 60000);
  const seconds = ((millis % 60000) / 1000).toFixed(0);
  return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
};

const millisToSS = (millis) => millis / 1000;

const uuidv4 = () =>
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    // eslint-disable-next-line no-bitwise
    const r = (Math.random() * 16) | 0;
    // eslint-disable-next-line no-bitwise,eqeqeq
    const v = c == "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

const isProduction = () => process.env.NODE_ENV === "production";

const createDebug = (name, ...message) => {
  debug(name)(...message);
};

// role: admin,editor  or editor  or reader
const handleModulePublic = ({ userRole, moduleId, moduleSetting }) => {
  if (!(userRole && moduleId && moduleSetting)) return false;
  if (!(moduleId in moduleSetting)) return false;
  const singleMs = moduleSetting[moduleId];
  // roles: "admin,developer" or "admin,developer,editor"
  const { isPublic, roles } = singleMs;
  if (!isPublic) return false;
  const rolesList = roles
    .split(",")
    .filter((r) => !!r)
    .map((r) => r.trim());
  const userRoleList = userRole
    .split(",")
    .filter((r) => !!r)
    .map((r) => r.trim());
  return userRoleList.filter((ele) => rolesList.includes(ele)).length > 0;
};

export {
  isEmpty,
  isNotEmpty,
  isTrue,
  isEquals,
  isNumeric,
  isFunction,
  leastOneValueEmpty,
  getDate,
  getTimeSince,
  ISOtransformYMD,
  getFormatUser,
  getYear,
  genRandomKey,
  encodeUrl,
  handleNameSpellCheck,
  safeGet,
  safeArray,
  bs64Encode,
  bs64Decode,
  bs64EncodeId,
  bs64DecodeId,
  exportImage,
  exportDataToCsv,
  downloadBlob,
  cvtDatasetLocale,
  isProductionDb,
  millisToMMSS,
  millisToSS,
  uuidv4,
  isProduction,
  createDebug,
  handleModulePublic,
};
